import { type FC, useContext } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import {
  useAdminTenantsApi,
  useAdminUsersApi,
} from "../../../hooks/useAdminApi";

export const AdminDashboardPage: FC = () => {
  const { currentUser } = useContext(AuthContext);

  // 統計情報取得
  const { data: tenantData, loading: tenantLoading } = useAdminTenantsApi();
  const { data: userData, loading: userLoading } = useAdminUsersApi();

  const dashboardItems = [
    {
      title: "テナント管理",
      description: "テナントの作成、編集、削除を行います",
      path: "/admin/tenants",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
      stat: tenantLoading ? null : tenantData?.total,
      statLabel: "件",
    },
    {
      title: "ユーザー管理",
      description: "ユーザーの管理を行います",
      path: "/admin/users",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
          />
        </svg>
      ),
      stat: userLoading ? null : userData?.total,
      statLabel: "名",
    },
    {
      title: "マスター管理",
      description: "スキルマスターの管理を行います",
      path: "/admin/masters/skills",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    {
      title: "フィーチャーフラグ管理",
      description: "機能フラグの ON/OFF を管理します",
      path: "/admin/feature-flags",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
          />
        </svg>
      ),
    },
    {
      title: "システム設定",
      description: "システム全体の設定を管理します",
      path: "/admin/settings",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      disabled: true,
    },
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <PageTitle>管理者ダッシュボード</PageTitle>

      {/* ウェルカムカード */}
      <div className="mb-8">
        <div className="card bg-base-100 shadow-md">
          <div className="card-body">
            <h2 className="card-title text-color-title">
              ようこそ、{currentUser?.familyName} {currentUser?.givenName}さん
            </h2>
            <p className="text-gray-600">
              システム管理者権限でシステムの管理を行うことができます。
            </p>
          </div>
        </div>
      </div>

      {/* 統計情報 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-color-title mb-4">
          システム統計
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat bg-base-100 rounded-lg shadow-md">
            <div className="stat-figure text-primary">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="stat-title">テナント数</div>
            <div className="stat-value text-primary">
              {tenantLoading ? "..." : tenantData?.total || 0}
            </div>
          </div>

          <div className="stat bg-base-100 rounded-lg shadow-md">
            <div className="stat-figure text-success">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-title">アクティブテナント</div>
            <div className="stat-value text-success">
              {tenantLoading
                ? "..."
                : tenantData?.items?.filter(t => t.isActive).length || 0}
            </div>
          </div>

          <div className="stat bg-base-100 rounded-lg shadow-md">
            <div className="stat-figure text-info">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div className="stat-title">総ユーザー数</div>
            <div className="stat-value text-info">
              {userLoading ? "..." : userData?.total || 0}
            </div>
          </div>
        </div>
      </div>

      {/* 管理メニュー */}
      <h3 className="text-lg font-semibold text-color-title mb-4">
        管理メニュー
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardItems.map(item =>
          item.disabled ? (
            <div
              key={item.path}
              className="card bg-base-100 shadow-md opacity-50 cursor-not-allowed"
            >
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-100 text-gray-400">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-400">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="badge badge-ghost">近日公開予定</span>
                </div>
              </div>
            </div>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              className="card bg-base-100 shadow-md hover:shadow-lg hover:border-primary border border-transparent transition-all group"
            >
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-color-title group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
                {item.stat !== undefined && item.stat !== null && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-2xl font-bold text-primary">
                      {item.stat}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">
                      {item.statLabel}
                    </span>
                  </div>
                )}
              </div>
            </Link>
          ),
        )}
      </div>

      {/* 注意事項 */}
      <div className="mt-8">
        <div className="alert alert-warning">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h3 className="font-bold">重要な注意事項</h3>
            <div className="text-sm">
              管理者機能は慎重に使用してください。テナントやユーザーの削除は元に戻すことができません。
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
